import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { cn } from '@/lib/utils';

/**
 * A sheet line edited in place (decision Forge FDE, F1): click to edit, Enter
 * or leaving the field saves, Escape cancels. No AI call: the text is the
 * user's. An emptied required line keeps its previous text.
 */
export const EditableText = ({ value, onSave, editable, label, className, required = true, maxLength }: {
  value: string;
  onSave: (value: string) => void;
  editable: boolean;
  /** What the line is, for screen readers ("Le cap", "Entrée Postes visés"…). */
  label: string;
  className?: string;
  required?: boolean;
  maxLength?: number;
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const field = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  useEffect(() => {
    if (!editing || !field.current) return;
    field.current.focus();
    field.current.select();
  }, [editing]);

  if (!editable || !editing) {
    // A span, not a <button>: browsers lay buttons out as blocks, while a
    // sheet line must wrap with the sentence around it.
    return editable
      ? <span
          role="button"
          tabIndex={0}
          aria-label={`Modifier : ${label}`}
          // Quiet at rest; hovering tints exactly the text that can be written.
          className={cn('-mx-0.5 cursor-text rounded px-0.5 transition-colors duration-150 ease-out hover:bg-domino-cardBorder/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-domino-prune', className)}
          onClick={() => setEditing(true)}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            setEditing(true);
          }}
        >
          {value || <span className="text-domino-inkSoft">Ajouter…</span>}
        </span>
      : <span className={className}>{value}</span>;
  }

  const save = () => {
    setEditing(false);
    const next = draft.trim();
    if (next === value.trim() || (required && !next)) return;
    onSave(next);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Escape') {
      setDraft(value);
      setEditing(false);
    } else if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      save();
    }
  };

  return <textarea
    ref={field}
    aria-label={label}
    value={draft}
    maxLength={maxLength}
    rows={Math.min(6, Math.max(1, Math.ceil(draft.length / 70)))}
    onChange={(event) => setDraft(event.target.value)}
    onBlur={save}
    onKeyDown={onKeyDown}
    className={cn('w-full resize-none rounded-md border border-domino-cardBorder bg-domino-white px-2 py-1 text-sm text-domino-ink focus:outline-none focus:ring-2 focus:ring-domino-prune', className)}
  />;
};
